import { useEffect, useState, useLayoutEffect } from 'react'

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

type Props = {
  isOpen: boolean
  content: React.RefObject<HTMLInputElement> | any
}

const useCollapse = ({ isOpen, content }: Props) => {
  const [height, setHeight] = useState('0')
  const [overflow, setOverflow] = useState('hidden')
  const [visibility, setVisibility] = useState('hidden')
  const [isFirstRender, setIsFirstRender] = useState(true)

  const getHeight = (content: React.RefObject<HTMLInputElement>) => {
    if (content?.current?.scrollHeight) {
      return `${content.current.scrollHeight}px`
    }
    return '0px'
  }

  const setIsExpandedStyle = () => {
    setHeight('auto')
    setOverflow('visible')
    setVisibility('visible')
  }

  const setIsCollapsedStyle = () => {
    setVisibility('hidden')
  }

  useIsomorphicLayoutEffect(() => {
    if (isOpen) {
      setVisibility('visible')
      if (isFirstRender) {
        setHeight('auto')
        setOverflow('visible')
      } else {
        setHeight(getHeight(content))
      }
    } else if (!isFirstRender) {
      setHeight(getHeight(content))
      // Set collapsed style after setting height to enable smooth transition based on height
      window.requestAnimationFrame(() => {
        // Setting these properties will start transition from measured height to 0
        setTimeout(() => {
          // Setting these properties will start transition from measured height to 0
          setHeight('0')
          setOverflow('hidden')
        })
      })
    }
  }, [isOpen])

  useEffect(() => {
    setIsFirstRender(false)
  }, [])

  return {
    setIsExpandedStyle,
    setIsCollapsedStyle,
    style: {
      overflow,
      visibility,
      height,
    },
  }
}

export default useCollapse
